import { useCallback, useRef, useState } from "react";
import useAgencyHasAnimateImageWithPromptFeature from "./useAgencyHasAnimateImageWithPromptFeature";

import { LaunchError } from "../utils/errors";
import { store } from "../app/store";

/**
 * Manages open/close state for the animate-image preview modal and delegates
 * confirmation to the caller as `{ prompt, outroVideoUrl }`. Only agencies in
 * `agenciesFeatureFlags/animateImageWithPrompt` see the prompt modal; others
 * animate immediately without a user prompt or outro.
 */
export const useAnimateImageWithPreview = () => {
  const hasAnimateImageWithPrompt = useAgencyHasAnimateImageWithPromptFeature();
  const [previewState, setPreviewState] = useState({
    isOpen: false,
    imageUrl: null,
  });
  const [isConfirming, setIsConfirming] = useState(false);
  const onConfirmRef = useRef(null);
  const inFlightUrlsRef = useRef(new Set());

  const openAnimatePreview = useCallback(({ imageUrl, onConfirm }) => {
    if (!imageUrl) return;
    onConfirmRef.current = onConfirm;
    setPreviewState({ isOpen: true, imageUrl });
  }, []);

  const runAnimateConfirm = useCallback(async (onConfirm, prompt) => {
    try {
      await onConfirm(prompt);
    } catch (error) {
      store.dispatch(
        LaunchError(error, false, [], {
          functionName: "useAnimateImageWithPreview",
          capture: "exception",
        })
      );
      console.error(
        "[useAnimateImageWithPreview] animate confirm failed:",
        error
      );
    }
  }, []);

  const requestAnimateImage = useCallback(
    ({ imageUrl, onConfirm }) => {
      if (!imageUrl || !onConfirm) return;
      if (inFlightUrlsRef.current.has(imageUrl)) return;

      if (hasAnimateImageWithPrompt) {
        openAnimatePreview({ imageUrl, onConfirm });
        return;
      }

      inFlightUrlsRef.current.add(imageUrl);
      void runAnimateConfirm(onConfirm, {
        prompt: "",
        outroVideoUrl: "",
      }).finally(() => {
        inFlightUrlsRef.current.delete(imageUrl);
      });
    },
    [hasAnimateImageWithPrompt, openAnimatePreview, runAnimateConfirm]
  );

  const cancelAnimate = useCallback(() => {
    setPreviewState({ isOpen: false, imageUrl: null });
    onConfirmRef.current = null;
  }, []);

  const confirmAnimate = useCallback(
    async (options) => {
      const prompt =
        typeof options === "string" ? options : String(options?.prompt || "");
      const outroVideoUrl =
        typeof options === "string" ? "" : String(options?.outroVideoUrl || "");
      const handler = onConfirmRef.current;
      const imageUrl = previewState.imageUrl;
      if (!handler) return;
      if (imageUrl && inFlightUrlsRef.current.has(imageUrl)) return;

      if (imageUrl) {
        inFlightUrlsRef.current.add(imageUrl);
      }
      setIsConfirming(true);

      const closePreview = () => {
        setIsConfirming(false);
        onConfirmRef.current = null;
        setPreviewState({ isOpen: false, imageUrl: null });
      };

      try {
        await runAnimateConfirm(handler, {
          prompt,
          outroVideoUrl,
          closePreview,
        });
      } finally {
        setIsConfirming(false);
        if (imageUrl) {
          inFlightUrlsRef.current.delete(imageUrl);
        }
      }
    },
    [previewState.imageUrl, runAnimateConfirm]
  );

  return {
    requestAnimateImage,
    openAnimatePreview,
    cancelAnimate,
    confirmAnimate,
    previewModalProps: {
      isOpen: previewState.isOpen,
      imageUrl: previewState.imageUrl,
      onClose: cancelAnimate,
      onConfirm: confirmAnimate,
      isSubmitting: isConfirming,
    },
  };
};
